<template>
  <div class="container">
    <div class="box has-background-light" v-if="user.is_applicant && !user.is_evaluator">
      <div class="columns">
        <div class="column"></div>
        <div class="column is-5 has-text-centered">
          <div class="card">
            <div
              class="card-header"
              style="padding: 10px; margin-top: 10px; background-color: #eb4b1b;"
            ></div>
            <div class="card-body" style="padding: 15px">
              <template
                v-if="
                  applicantReportStatus === 'checking' || applicantReportStatus === 'redirecting'
                "
              >
                <h6 class="title is-3">Give us a few seconds...</h6>
                <h6 class="subtitle is-4">There's a lot of data to load!</h6>
                <i class="fa fa-refresh fa-spin fa-4x"></i>
              </template>
              <template v-else-if="applicantReportStatus === 'not_found'">
                <h6 class="title is-3">Your report isn't ready yet</h6>
                <h6 class="subtitle is-4">
                  We don't have a submission report for your organization for the current round yet.
                  Please check back later.
                </h6>
              </template>
              <template v-else-if="applicantReportStatus === 'error'">
                <h6 class="title is-3">Something went wrong</h6>
                <h6 class="subtitle is-4">
                  We couldn't load your submission report. Please refresh the page, or contact us if
                  this continues.
                </h6>
              </template>
            </div>
            <div
              class="card-footer"
              style="padding: 10px; margin-top: 10px; background-color: #eb4b1b;"
            ></div>
          </div>
        </div>
        <div class="column"></div>
      </div>
    </div>
    <div v-if="user && user.is_evaluator">
      <div v-if="!assignedApplicationsLoaded" class="box has-background-light">
        <h4 class="title is-4"><i class="fa fa-refresh fa-spin"></i> Loading Your Assignments</h4>
      </div>
      <div v-if="assignedApplicationsLoaded" class="box has-background-light">
        <h4 class="title is-4">Your Assignments</h4>
        <h6 class="subtitle is-6">
          These submissions have been chosen specifically for you to evaluate. Please start here.
        </h6>
        <table class="table is-fullwidth is-striped has-background-light assignments">
          <thead>
            <tr>
              <th>Organization Name</th>
              <th class="has-text-left-aligned">Project Name</th>
              <th class="has-text-centered">Completed</th>
              <th class="has-text-centered">Follow-Up Made?</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="application in assignedApplications" :key="application.id">
              <td class="fixed-width">
                <router-link
                  :to="{ name: 'ApplicationReviewDetail', params: { applicationId: application.id } }"
                  >
                  {{ application.organization.name }}
                </router-link>
              </td>
              <td class="has-text-left-aligned">{{ application.projectName }}</td>W
              <td class="has-text-centered">
                <i class="fa fa-check" v-if="application.isEvaluatedByUser"></i>
              </td>
              <td class="has-text-centered">
                <i class="fa fa-check" v-if="application.hasCommitmentsByUser"></i>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <h4 class="title is-4">All Other Submissions</h4>
      <h6 class="subtitle is-6">
        Once you have completed your assigned proposals you are welcome to keep reviewing.
        <button class="toggleApps" @click="showAllApplications = !showAllApplications">
          {{ allAppsButtonText }}
        </button>
      </h6>
      <div v-if="showAllApplications">
        <div v-if="!applicationDataLoaded"><i class="fa fa-refresh fa-spin"></i> Loading...</div>
        <table class="table is-fullwidth is-striped others" v-if="applicationDataLoaded">
          <thead>
            <tr>
              <th>Organization Name</th>
              <th class="has-text-left-aligned">Project Name</th>
              <th class="has-text-left-aligned orgSub">Organization Submitted to</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="application in allApplications" :key="application.id">
              <td class="fixed-width">
                <router-link
                  :to="{
                    name: 'NonAssignedApplicationReviewDetail',
                    params: { applicationId: application.id },
                  }"
                  >{{ application.organization.name }}</router-link
                >
              </td>
              <td class="has-text-left-aligned">{{ application.projectName }}</td>
              <td class="has-text-left-aligned orgSub">{{ application.organizationSubmittedTo }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-if="assignedApplicationsLoaded" style="padding:56.25% 0 0 0;position:relative;">
        <iframe src="https://www.youtube.com/embed/g-ACy-aqBBE?si=SEbocxKzMYYxeWNs" title="YouTube video player" frameborder="0" referrerpolicy="strict-origin-when-cross-origin" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen style="position:absolute;top:0;left:0;width:100%;height:100%;"></iframe>
      </div>
      <div v-if="assignedApplicationsLoaded" style="margin-top: 20px">
        <h4 class="title is-4">Donate to UnfundedList</h4>
        <!-- donation widget -->
        <component is="script" src="https://secure.givelively.org/widgets/simple_donation/unfunded-list.js?show_suggested_amount_buttons=false&show_in_honor_of=false&address_required=false&has_required_custom_question=null" async></component>
        <div id="give-lively-widget" class="gl-simple-donation-widget"></div>
      </div>
    </div>
  </div>
</template>
<script>
import { mapState } from 'vuex'
import User from '@/services/users'
import Application from '../services/applications'
import Cohort from '@/services/cohorts'

export default {
  data() {
    return {
      assignedApplications: [],
      allApplications: [],
      allApplicationFilters: {
        pageSize: 200,
      },
      applicationDataLoaded: false,
      assignedApplicationsLoaded: false,
      showAllApplications: false,
      applicantReportStatus: 'checking',
    }
  },
  computed: {
    ...mapState(['userLoggedInStatus', 'user']),
    allAppsButtonText() {
      const verb = this.showAllApplications ? 'Hide' : 'Show'
      return `${verb} all applications`
    },
  },
  methods: {
    logout() {
      this.$store.commit('LOGOUT_USER')
      this.$router.push({ name: 'Login' })
    },
    async loadAllApplications() {
      const allApps = await Application.api.list({ filters: this.allApplicationFilters })

      this.allApplications = allApps.results.filter(
        application => !application.evaluators.includes(this.user.id),
      )
      this.applicationDataLoaded = true
    },
    async loadAssignments() {
      const activeCohortID = await Cohort.api.activeID()
      const userApps = await User.api.listApplications(this.user.id)
      this.assignedApplications = userApps.filter(
        application => application.cohort.id === activeCohortID,
      )
      this.assignedApplicationsLoaded = true
    },
    async checkApplicantReport() {
      this.applicantReportStatus = 'checking'

      if (!this.user.organization_ref) {
        this.applicantReportStatus = 'error'
        return
      }

      try {
        const activeCohortID = await Cohort.api.activeID()
        const everyReport = await Application.api.getAllApplications()
        const report = everyReport.find(
          r =>
            r.organizationName === this.user.organization_ref.name &&
            r.cohort === activeCohortID,
        )

        if (report) {
          this.applicantReportStatus = 'redirecting'
          this.$router.replace({
            name: 'ApplicationReportDetail',
            params: { applicationId: report.id },
          })
        } else {
          this.applicantReportStatus = 'not_found'
        }
      } catch (err) {
        this.applicantReportStatus = 'error'
        this.$Alert.alert({
          type: 'error',
          message: `Error loading submission report`,
          timeout: 6000,
        })
      }
    },
  },
  async created() {
    if (this.user.is_applicant && !this.user.is_evaluator) {
      this.checkApplicantReport()
    }
    await this.loadAssignments()
    this.loadAllApplications()
  },
}
</script>

<style scoped>
.fixed-width {
  word-wrap: break-word;
  width: 350px;
}
.others .orgSub {
  min-width: 250px;
}
.toggleApps {
  height: 2em;
  border-radius: 4px;
  border-width: 0px;
  background-color: #eb4a19;
  color: white;
}
</style>
